import React from 'react';
import { ArrowRight, BookOpen, BarChart3, Briefcase, CheckCircle } from 'lucide-react';

export default function LandingPage({ onNavigate }) {
  const stages = [
    {
      id: 1,
      title: 'Financial Literacy for Clients',
      subtitle: 'Personality-aware AI coaching for unadvised retail investors',
      status: 'LIVE NOW',
      statusColor: 'status-positive',
      bgColor: 'background-stage1',
      icon: BookOpen,
      description: 'Build financial confidence through personalized AI coaching that adapts to your personality and learning style.',
      features: [
        'Personality assessment (BFI-2)',
        'Financial literacy baseline',
        'AI coach with reflective learning',
        '7 curriculum modules',
        'Progress tracking dashboard'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/187d10af-6d04-4849-a852-006c7bef1cb9/images/964e4180f5400bcc1a35972ae039c7f15a5071332e3f4ae58b3bc41d4aa0cd5f.png'
    },
    {
      id: 2,
      title: 'Personal Financial Intelligence',
      subtitle: 'Continuous monitoring for self-advised retail investors',
      status: 'COMING SOON',
      statusColor: 'status-warning',
      bgColor: 'background-stage2',
      icon: BarChart3,
      description: 'Transform financial advice from a credence good into an experience good. See exactly how your plan matches reality.',
      features: [
        'Link accounts via Plaid (read-only)',
        'Plan vs execution tracking',
        'Fee iceberg analysis',
        'Behavior signal detection',
        'Tax opportunity surfacing',
        'Quarterly review reports'
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'
    },
    {
      id: 3,
      title: 'Intelligence for Planners',
      subtitle: 'Audit-traceable second opinions for CFP professionals',
      status: 'COMING SOON',
      statusColor: 'status-info',
      bgColor: 'background-stage3',
      icon: Briefcase,
      description: 'Professional workstation with audit-traceable AI recommendations, RAG-powered insights, and computational fiduciary standard.',
      features: [
        'Per-client knowledge bases',
        'Self-RAG with citation tracking',
        'Suitability checking (FINRA/SEC)',
        'Tamper-proof audit trails',
        'Firm-level analytics',
        'Compliance officer access'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/187d10af-6d04-4849-a852-006c7bef1cb9/images/17821e8a17ad6aa60d8483d09fdbee31f05b0088e982cf13f137632606d65063.png'
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/187d10af-6d04-4849-a852-006c7bef1cb9/images/e86eff2cd2a321cb5f0d930026ee66c3aa765084a0567bb3f94ee0d77abfb9d1.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="text-center">
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-text-primary mb-6">
              Nudge
            </h1>
            <p className="text-xl sm:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-4">
              AI-powered financial guidance across three intelligent stages
            </p>
            <p className="text-base text-text-muted max-w-2xl mx-auto">
              From building financial literacy to professional planning workstations
            </p>
          </div>
        </div>
      </div>

      {/* Understanding the Research - Visual Diagrams Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl tracking-tight text-text-primary mb-3">
            Understanding the Research
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Explore the data and frameworks behind Nudge's approach to financial advice
          </p>
        </div>

        <div className="space-y-12">
          {/* Main Feature: The AI-Mediated Advisory Pipeline */}
          <div className="bg-white border-2 border-brand rounded-lg p-8 shadow-lg">
            <div className="mb-6">
              <h3 className="font-serif text-2xl text-text-primary mb-2">
                The AI-Mediated Advisory Pipeline
              </h3>
              <p className="text-text-secondary text-sm">
                How AI continuously improves financial advice quality, transparency, and access
              </p>
            </div>
            <div className="flex justify-center bg-gradient-to-br from-background-stage1 to-white rounded-lg p-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/cjl4rjuc_AI_Financial_Advice_Flowchart.png"
                alt="AI-Mediated Advisory Pipeline"
                className="w-full max-w-5xl rounded"
              />
            </div>
          </div>

          {/* Five Market Failures */}
          <div className="bg-white border border-border-DEFAULT rounded-lg p-8 shadow-sm">
            <div className="mb-6">
              <h3 className="font-serif text-2xl text-text-primary mb-2">
                The Five Market Failures in Financial Advice
              </h3>
              <p className="text-text-secondary text-sm">
                Structural problems in the advisory market that AI can help address
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/bgqixlnc_five_market_failures_ai_solutions.svg"
                alt="Five Market Failures and AI Solutions"
                className="w-full max-w-4xl"
              />
            </div>
          </div>

          {/* AI Behavioral Biases - Updated Version */}
          <div className="bg-white border border-border-DEFAULT rounded-lg p-8 shadow-sm">
            <div className="mb-6">
              <h3 className="font-serif text-2xl text-text-primary mb-2">
                AI as Debiaser or Bias Amplifier
              </h3>
              <p className="text-text-secondary text-sm">
                How AI design choices determine whether it helps or harms investor behavior
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/qt2zbotz_AI_Behavioral_Biases_Flowchart.svg"
                alt="AI Behavioral Biases Decision Flow"
                className="w-full max-w-4xl"
              />
            </div>
          </div>

          {/* Supporting Diagrams - Now Full Width for Better Readability */}
          <div className="space-y-8">
            {/* How AI Advisor Works */}
            <div className="bg-white border border-border-DEFAULT rounded-lg p-8 shadow-sm">
              <div className="mb-6">
                <h3 className="font-serif text-xl text-text-primary mb-2 text-center">
                  How the AI Advisor Works
                </h3>
              </div>
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/jkx6dqa0_how_ai_advisor_works_flow.svg"
                  alt="AI Advisor Workflow"
                  className="w-full max-w-3xl"
                />
              </div>
            </div>

            {/* Who Benefits Most */}
            <div className="bg-white border border-border-DEFAULT rounded-lg p-8 shadow-sm">
              <div className="mb-6">
                <h3 className="font-serif text-xl text-text-primary mb-2 text-center">
                  Who Benefits Most from AI Advice
                </h3>
              </div>
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/05v09wnv_who_benefits_most_from_ai_advice.svg"
                  alt="Who Benefits from AI Advice"
                  className="w-full max-w-3xl"
                />
              </div>
            </div>

            {/* Human-AI Partnership */}
            <div className="bg-white border border-border-DEFAULT rounded-lg p-8 shadow-sm">
              <div className="mb-6">
                <h3 className="font-serif text-xl text-text-primary mb-2 text-center">
                  Human-AI Advisory Partnership
                </h3>
              </div>
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/rc7e21ep_human_ai_advisory_partnership.svg"
                  alt="Human-AI Partnership Model"
                  className="w-full max-w-3xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Research Program Explainer */}
      <div className="bg-background-stage1 border-y border-border-DEFAULT">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl tracking-tight text-text-primary mb-4">
              Can AI Fix Financial Advice?
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Explore the research behind Nudge: how AI could repair — or worsen — the way ordinary people get money advice
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Key Points */}
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-status-critical flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">The Problem</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      Poor financial advice costs investors 2.5-3% annually — erasing over a third of nest eggs over 25 years
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-status-warning flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Why It Happens</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      Five structural flaws + behavioral biases (procrastination, overconfidence, loss aversion) work against you
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-status-positive flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">The Solution</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      An "Algorithmic Fiduciary" — AI designed to act in your best interest, auditable and transparent
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-status-info flex items-center justify-center text-white font-bold text-sm">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">The Catch</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      AI can be a debiaser OR a bias amplifier — design and auditing are everything
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="https://customer-assets.emergentagent.com/job_nudge-ai-2/artifacts/hukhaa8p_Nudge_Explainer%20%281%29.pptx"
                download
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-md font-semibold transition-colors"
                data-testid="download-explainer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Full Research Explainer
              </a>
            </div>

            {/* Right: Visual */}
            <div className="relative">
              <div className="bg-white border-2 border-border-DEFAULT rounded-lg p-8 shadow-lg">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center text-white font-serif text-xl">
                      N
                    </div>
                    <div>
                      <h4 className="font-serif text-xl text-text-primary">Nudge Research Program</h4>
                      <p className="text-xs text-text-muted">Harry Vadalkar, CFA, CFP · Marc Millic</p>
                    </div>
                  </div>

                  <div className="border border-border-DEFAULT rounded p-4 bg-background-stage1">
                    <p className="font-mono text-2xl text-status-critical mb-2">2.5-3%</p>
                    <p className="text-sm text-text-secondary">Annual loss from poor advice</p>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-border-DEFAULT rounded p-3 bg-background">
                      <p className="text-xs text-text-muted mb-1">Stage 1</p>
                      <p className="font-semibold text-sm text-text-primary">Teach</p>
                    </div>
                    <div className="border border-border-DEFAULT rounded p-3 bg-background">
                      <p className="text-xs text-text-muted mb-1">Stage 2</p>
                      <p className="font-semibold text-sm text-text-primary">Monitor</p>
                    </div>
                    <div className="border border-border-DEFAULT rounded p-3 bg-background">
                      <p className="text-xs text-text-muted mb-1">Stage 3</p>
                      <p className="font-semibold text-sm text-text-primary">Augment</p>
                    </div>
                  </div>

                  <p className="text-xs text-text-muted italic text-center pt-4">
                    One system, three coordinated stages
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stages Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="space-y-16">
          {stages.map((stage, index) => (
            <div 
              key={stage.id}
              className="group"
              data-testid={`stage-${stage.id}-card`}
            >
              <div className={`bg-${stage.bgColor} border border-border-DEFAULT rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl`}>
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Content Side */}
                  <div className={`p-12 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <stage.icon className="w-8 h-8 text-brand" />
                      <span className={`px-3 py-1 text-xs font-semibold tracking-wide rounded-full bg-${stage.statusColor} text-white`}>
                        {stage.status}
                      </span>
                    </div>
                    
                    <h2 className="font-serif text-3xl tracking-tight text-text-primary mb-2">
                      Stage {stage.id}
                    </h2>
                    <h3 className="font-serif text-2xl text-brand mb-3">
                      {stage.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-6">
                      {stage.subtitle}
                    </p>
                    <p className="text-text-primary leading-relaxed mb-8">
                      {stage.description}
                    </p>

                    <div className="space-y-3 mb-8">
                      {stage.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-status-positive mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {stage.id === 1 ? (
                      <button
                        onClick={() => onNavigate('stage1')}
                        className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-md font-semibold transition-colors"
                        data-testid="stage1-launch-button"
                      >
                        Launch Stage 1
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        disabled
                        className="inline-flex items-center gap-2 bg-text-muted text-white px-6 py-3 rounded-md font-semibold cursor-not-allowed opacity-60"
                      >
                        Coming Soon
                      </button>
                    )}

                    {stage.id === 1 && (
                      <p className="mt-6 text-xs text-text-muted italic">
                        Educational content, not investment advice
                      </p>
                    )}
                  </div>

                  {/* Image Side */}
                  <div className={`relative h-64 md:h-auto ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                    <img 
                      src={stage.image}
                      alt={stage.title}
                      className="w-full h-full object-cover"
                    />
                    {stage.status === 'COMING SOON' && (
                      <div className="absolute inset-0 bg-text-primary bg-opacity-40 backdrop-blur-sm flex items-center justify-center">
                        <div className="text-center text-white p-8">
                          <p className="font-serif text-2xl mb-2">Under Development</p>
                          <p className="text-sm">Check back soon for updates</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-background-stage1 border-t border-border-DEFAULT mt-24">
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <p className="text-text-muted text-sm">
            © 2025 Nudge Financial Advisory Platform. All rights reserved.
          </p>
          <p className="text-text-muted text-xs mt-2">
            This platform provides educational content only and does not constitute investment advice.
          </p>
        </div>
      </div>
    </div>
  );
}
